import { Injectable, OnDestroy } from '@angular/core';
import {
    SmartCaptcha,
    SmartCaptchaRenderOptions,
    SmartCaptchaResponse,
    SmartCaptchaSubscribeEvent,
    WidgetId,
} from '../types/yandex-captcha';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, defer, exhaustMap, filter, of, throwError } from 'rxjs';
import { DialogService } from './dialog.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class SmartCaptchaService implements OnDestroy {
    private smartCaptcha: SmartCaptcha | null = null;
    private widgetId: WidgetId | null;
    private readonly events: Array<SmartCaptchaSubscribeEvent> = Object.values(SmartCaptchaSubscribeEvent);
    private readonly eventEmitter: Subject<SmartCaptchaSubscribeEvent> = new Subject();
    private readonly unsubscribeFunctions: Array<() => void> = [];

    constructor(private readonly http: HttpClient, private readonly dialogService: DialogService) {
        this.initialize();
    }

    ngOnDestroy(): void {
        if (this.unsubscribeFunctions.length) {
            this.unsubscribeFunctions.forEach((unsubscriber) => unsubscriber());
        }
    }

    private initialize(): void {
        if ('smartCaptcha' in window) {
            this.smartCaptcha = window.smartCaptcha as SmartCaptcha;
        } else {
            this.dialogService.notifyInSnackBar('Yandex SmartCaptcha не подключена', 'error');
        }
    }

    render(
        container: HTMLDivElement,
        options: Omit<SmartCaptchaRenderOptions, 'sitekey'> = { hl: 'ru' }
    ): Observable<SmartCaptchaSubscribeEvent> {
        if (this.smartCaptcha) {
            this.widgetId = this.smartCaptcha.render(container, { sitekey: environment.smartCaptchaPublicKey, ...options });
            this.subscribe();
        }

        return this.eventEmitter.asObservable().pipe(
            filter(() => Boolean(this.smartCaptcha && this.widgetId !== null)),
            exhaustMap((event) => defer(() => this.validateResponseHandler(event)))
        );
    }

    private subscribe(): void {
        this.events.forEach((event) =>
            this.unsubscribeFunctions.push(this.smartCaptcha!.subscribe(this.widgetId!, event, () => this.eventEmitter.next(event)))
        );
    }

    private validate(token: string): Observable<SmartCaptchaResponse> {
        return this.http.get<SmartCaptchaResponse>(`${environment.api}/validate-captcha`, {
            params: new HttpParams({ fromObject: { token } }),
        });
    }

    private validateResponseHandler(event: SmartCaptchaSubscribeEvent): Observable<SmartCaptchaSubscribeEvent> {
        if (event === SmartCaptchaSubscribeEvent.Success) {
            return this.validate(this.smartCaptcha!.getResponse(this.widgetId!)).pipe(
                exhaustMap(({ status, message }) =>
                    defer(() => (status === 'ok' ? of(event) : throwError(() => new Error(message || 'Ошибка обработки капчи'))))
                )
            );
        } else {
            return of(event);
        }
    }
}
